import './comparison-chart.scss';
import { loadScript } from '../../utilities/js/helper';
import debounce from 'lodash.debounce';

/**
 * ComparisonChart class renders and manages a radar/comparison chart
 * visualising quantitative data on multiple axes for various materials.
 * Handles drawing the axes, polygons, tooltips and interactive controls.
 */
class ComparisonChart {
    constructor ($element) {
        this.$comparisonChart = $element;
        this.darkmode = this.$comparisonChart.dataset.darkmode;
        this.chartInstance = null;
        this.polygonsInOriginalOrder = [];
        this.polygonsInAreaOrder = [];
        this.polygonRegistry = {};
        this.draw = null;
        this.horizontalAxisLabels = [];
        this.$subline = $element.querySelector('[data-comparsion-chart="subline"]');
    }

    /**
     * Initializes the comparison chart by parsing data, preparing the SVG, and wiring controls.
     */
    initialize () {
        const $chartDiv = this.$comparisonChart.querySelector('[data-comparsion-chart="chart"]');
        if (!$chartDiv) return;

        // Parse JSON data containing materials and their values
        const comparisonData = $chartDiv.dataset.comparisonData ? JSON.parse($chartDiv.dataset.comparisonData) : {};
        this.comparisonData = comparisonData;

        if (!this.comparisonData || !this.comparisonData.materials) return;

        const svgSize = 330;
        const center = { x: svgSize / 2, y: svgSize / 2 };
        const labelsSet = [];

        // Collect all unique axis labels (attributes) from the dataset
        (this.comparisonData.materials || []).forEach((material) => {
            (material.data || []).forEach((row) => {
                if (!labelsSet.includes(row.attribute)) labelsSet.push(row.attribute);
            });
        });

        const labels = labelsSet;
        const numAxes = labels.length;

        // Prepare normalized dataset for the chart polygons
        const datasetsUnsorted = (this.comparisonData.materials || []).map((material, idx) => {
            const dataMap = {};
            (material.data || []).forEach((row) => {
                dataMap[row.attribute] = parseFloat((row.value + '').replace(',', '.'));
            });
            const values = labels.map((attr) => (isFinite(dataMap[attr]) ? dataMap[attr] : 0));
            return { name: material.name, values, rawIndex: idx };
        });

        const rMaxPolygon = 120;

        // Find the maximum per axis for normalization
        const maxPerAxis = labels.map((attr, i) =>
            Math.max(...datasetsUnsorted.map((datasets) => datasets.values[i] || 0))
        );

        // Calculate area for each dataset's polygon, used to paint larger polygons behind the smaller
        datasetsUnsorted.forEach((datasets) => {
            const points = datasets.values.map((v, i) => {
                const axisMax = Math.max(...datasetsUnsorted.map((data) => data.values[i] || 0), 1);
                const norm = v / axisMax;
                const angle = (Math.PI * 2 * i) / numAxes - Math.PI / 2;
                return [
                    center.x + rMaxPolygon * norm * Math.cos(angle),
                    center.y + rMaxPolygon * norm * Math.sin(angle)
                ];
            });
            datasets._area = this.polygonArea(points);
        });

        const datasets = datasetsUnsorted.slice();

        // Create the SVG root for the radar chart using svg.js library.
        // Width/height come from CSS (width: 100%, height: auto) so the SVG is
        // sized intrinsically from its viewBox instead of being stretched by the
        // flex row, which would letterbox the content and add empty bands.
        const draw = window.SVG()
            .addTo(this.$comparisonChart.querySelector('[data-comparsion-chart="chart"]'))
            .attr('preserveAspectRatio', 'xMidYMid meet');

        // Define a color palette for polygons
        const palette = [
            { fill: 'rgba(54,162,235,0.3)', stroke: '#3692eb' },
            { fill: 'rgba(235,54,54,0.23)', stroke: '#eb3636' },
            { fill: 'rgba(40,167,69,0.28)', stroke: '#28a745' },
            { fill: 'rgba(253,160,6,0.25)', stroke: '#fda006' },
            { fill: 'rgba(106,58,255,0.22)', stroke: '#6a3aff' }
        ];

        this.drawAxes(draw, labels, center, rMaxPolygon);
        this.drawPolygons(draw, datasets, palette, center, numAxes, rMaxPolygon, maxPerAxis);
        this.setupControls(datasets, palette);

        this.draw = draw;
        this.updateLayout();

        // Re-measure once web fonts have loaded (the labels use Open Sans, whose
        // metrics differ from the fallback) and whenever the viewport changes.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.updateLayout());
        }
        window.addEventListener('resize', debounce(() => this.updateLayout(), 100), { passive: true });
    }

    /**
     * Recomputes the two layout adjustments that depend on the rendered size:
     * fitting the viewBox to the content and aligning the chart with the controls.
     */
    updateLayout () {
        if (!this.draw) return;
        this.adjustHorizontalLabels();
        this.fitViewBoxToContent(this.draw);
        this.alignChartWithControls();
    }

    /**
     * Fits the SVG viewBox to the bounding box of the rendered content.
     * The fixed padding used previously clipped the labels sitting at the end
     * of the right/bottom axes while wasting space on the other sides. Measuring
     * the real content bounds keeps all labels visible with a minimal, uniform
     * margin on every side.
     * @param {SVG} draw - The svg.js root context whose viewBox is adjusted.
     * @param {number} padding - Uniform margin (in user units) around the content.
     */
    fitViewBoxToContent (draw, padding = 8) {
        const box = draw.bbox();
        if (!box || !isFinite(box.width) || !isFinite(box.height) || box.width === 0 || box.height === 0) return;

        draw.viewbox(
            box.x - padding,
            box.y - padding,
            box.width + padding * 2,
            box.height + padding * 2
        );
    }

    /**
     * Vertically aligns the chart with the controls column.
     * When a subline is present it pushes the controls down inside the list
     * column, while the chart column has nothing above it. The chart therefore
     * has to move down by exactly that amount to start at the same height as the
     * controls. The offset is applied as a margin on the chart container itself
     * (not on the SVG) so it never interferes with the SVG's internal sizing.
     * It is measured directly from the DOM (distance between the top of the
     * chart column and the top of the controls), so it stays exact for any
     * subline length and collapses to 0 when no subline exists or when the
     * columns are stacked on small screens.
     */
    alignChartWithControls () {
        const $chartContainer = this.$comparisonChart.querySelector('[data-comparsion-chart="chart"]');
        const $controls = this.$comparisonChart.querySelector('[data-comparsion-chart="controls"]');

        // Reset first so a previously applied offset never distorts the measurement.
        if ($chartContainer) $chartContainer.style.marginTop = '';

        if (!this.$subline || !$chartContainer || !$controls) return;

        const offset = $controls.getBoundingClientRect().top - $chartContainer.getBoundingClientRect().top;
        if (offset > 0) $chartContainer.style.marginTop = `${offset}px`;
    }

    /**
     * Draws axes with labels for each attribute on the radar chart.
     * @param {SVG} draw - The svg.js context.
     * @param {string[]} labels - The attribute labels for the axes.
     * @param {{x: number, y: number}} center - The center point of the chart.
     * @param {number} rMaxPolygon - The maximum radius for the axes.
     */
    drawAxes (draw, labels, center, rMaxPolygon) {
        const axisColor = this.darkmode ? '#444' : '#DDDBD3';
        const textColor = this.darkmode ? '#fff' : '#000';

        const axisGroup = draw.group();
        labels.forEach((labelText, i) => {
            const angle = (Math.PI * 2 * i) / labels.length - Math.PI / 2;
            const axisLength = rMaxPolygon + 20;
            const x2 = center.x + axisLength * Math.cos(angle);
            const y2 = center.y + axisLength * Math.sin(angle);
            axisGroup.line(center.x, center.y, x2, y2).stroke({ color: axisColor, width: 1 });

            // Split label into multiple lines if needed
            const maxCharsPerLine = 20;
            const words = labelText.split(' ');
            const lines = [];
            let line = '';
            words.forEach((word) => {
                if ((line + ' ' + word).trim().length > maxCharsPerLine) {
                    lines.push(line.trim());
                    line = word;
                } else {
                    line += ' ' + word;
                }
            });
            if (line) lines.push(line.trim());

            const extraOffset = lines.length === 1 ? 20 : lines.length === 2 ? 30 : 40;
            const lxAdjusted = center.x + (axisLength + 15 + extraOffset) * Math.cos(angle);
            const lyAdjusted = center.y + (axisLength + 15 + extraOffset) * Math.sin(angle);

            // Render the text label
            const label = axisGroup
                .text((add) => {
                    lines.forEach((l, idx) => add.tspan(l).dy(idx ? 14 : 0).newLine());
                })
                .font({ size: 13, family: 'Arial', anchor: 'middle' })
                .fill(textColor)
                .center(lxAdjusted, lyAdjusted)
                .addClass('comparsion-chart__axis-label');
            label.back();

            // A perfectly horizontal axis (its line points straight left or
            // right) ends level with the middle-anchored label, so half the text
            // overlaps the line. This only happens when the axis count is a
            // multiple of 4. Remember those labels so their horizontal position
            // can be nudged clear of the line end in adjustHorizontalLabels().
            if (Math.abs(Math.sin(angle)) < 0.01) {
                this.horizontalAxisLabels.push({
                    label,
                    direction: Math.cos(angle) >= 0 ? 1 : -1,
                    lineEndX: center.x + axisLength * Math.cos(angle)
                });
            }
        });
        axisGroup.back();
    }

    /**
     * Nudges the labels of horizontal axes so the text starts a fixed gap after
     * the end of the axis line instead of overlapping it. The shift is derived
     * from the measured text width, so it stays exact once the web font has
     * loaded (this runs again from updateLayout on that event and on resize).
     * @param {number} gap - Minimum space (in user units) between line and text.
     */
    adjustHorizontalLabels (gap = 12) {
        this.horizontalAxisLabels.forEach(({ label, direction, lineEndX }) => {
            const halfWidth = label.bbox().width / 2;
            label.cx(lineEndX + direction * (gap + halfWidth));
        });
    }

    /**
     * Draws the polygons (datasets), their interaction elements, and tooltips on the chart.
     * Handles hover and focus events for accessibility.
     * @param {SVG} draw - The svg.js context.
     * @param {Array} datasets - The data for each polygon.
     * @param {Array} palette - The color palette for the polygons.
     * @param {{x: number, y: number}} center - The center of the chart.
     * @param {number} numAxes - Number of axes.
     * @param {number} rMaxPolygon - Max polygon radius.
     * @param {Array} maxPerAxis - The maximum value per axis for normalization.
     */
    drawPolygons (draw, datasets, palette, center, numAxes, rMaxPolygon, maxPerAxis) {
        const polygonsData = [];

        datasets.forEach((dataset) => {
            const { fill, stroke } = palette[dataset.rawIndex % palette.length];

            // Calculate coordinates for each axis/vertex for current dataset
            const points = dataset.values.map((v, i) => {
                const axisMax = maxPerAxis[i] > 0 ? maxPerAxis[i] : 1;
                const norm = v / axisMax;
                const angle = (Math.PI * 2 * i) / numAxes - Math.PI / 2;
                return [
                    Math.round(center.x + rMaxPolygon * norm * Math.cos(angle)),
                    Math.round(center.y + rMaxPolygon * norm * Math.sin(angle))
                ];
            });
            const polygonString = points.map((pt) => pt.join(',')).join(',');
            const area = this.polygonArea(points);

            polygonsData.push({ dataset, points, fill, stroke, polygonString, area });
        });

        // Ensure larger polygons are rendered underneath smaller ones
        polygonsData.sort((a, b) => b.area - a.area);
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        polygonsData.forEach(({ dataset, points, fill, stroke, polygonString, area }) => {
            const polygon = draw
                .polygon(polygonString)
                .stroke({ color: stroke, width: 1 })
                .fill(fill)
                .attr('data-name', dataset.name)
                .attr('data-original-index', dataset.rawIndex);

            polygon.addClass(`comparsion-chart__radar-polygon-${dataset.rawIndex}`);

            const dotElements = [];
            const tooltipElements = [];
            const tooltipColor = this.darkmode ? '#fff' : '#000';

            // Add invisible interaction dots and tooltips at each vertex
            points.forEach((point, i) => {
                const dot = draw
                    .circle(8)
                    .center(point[0], point[1])
                    .attr({ fill: stroke, stroke: '#fff', opacity: 0 });

                const tooltip = draw
                    .text(String(dataset.values[i]))
                    .font({ size: 11, family: 'Open sans', anchor: 'middle' })
                    .fill(tooltipColor)
                    .center(point[0], point[1] - 16)
                    .attr('opacity', 0);

                dotElements.push(dot);
                tooltipElements.push(tooltip);
            });

            const togglePolygon = () => {
                const isActive = polygon.hasClass('is-hovered');
                this.deactivateAll();
                if (!isActive) this.showPolygon(polygon, dotElements, tooltipElements);
            };

            // Assign touch and mouse/keyboard events for interaction
            if (isTouchDevice) {
                polygon.on('touchstart', (e) => {
                    e.preventDefault();
                    togglePolygon();
                });
                dotElements.forEach((dot) => {
                    dot.on('touchstart', (e) => {
                        e.preventDefault();
                        togglePolygon();
                    });
                });
            } else {
                polygon.on('mouseenter', () => this.showPolygon(polygon, dotElements, tooltipElements));
                polygon.on('mouseleave', () => this.hidePolygon(polygon, dotElements, tooltipElements));
                polygon.on('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        togglePolygon();
                    }
                });
                dotElements.forEach((dot) => {
                    dot.on('mouseenter', () => this.showPolygon(polygon, dotElements, tooltipElements));
                    dot.on('mouseleave', () => this.hidePolygon(polygon, dotElements, tooltipElements));
                    dot.on('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            togglePolygon();
                        }
                    });
                });
            }

            this.polygonsInOriginalOrder.push(polygon);
            this.polygonsInAreaOrder.push(polygon);
            this.polygonRegistry[dataset.rawIndex] = { polygon, dots: dotElements, tooltips: tooltipElements };
        });
    }

    /**
     * Shows a polygon with its dots and tooltips (sets opacity, brings to front).
     * @param {SVGPolygon} polygon - The SVG polygon element.
     * @param {Array} dots - Array of SVG circle elements acting as dots on the vertices.
     * @param {Array} tooltips - Array of SVG text elements as tooltips.
     */
    showPolygon (polygon, dots, tooltips) {
        polygon.addClass('comparsion-chart__polygon--hovered');
        dots.forEach((dot) => dot.attr({ opacity: 1 }).front());
        tooltips.forEach((tooltip) => tooltip.attr({ opacity: 1 }).front());
    }

    /**
     * Hides a polygon's dots and tooltips (sets opacity to 0).
     * @param {SVGPolygon} polygon - The SVG polygon element.
     * @param {Array} dots - Array of SVG circle elements acting as dots on the vertices.
     * @param {Array} tooltips - Array of SVG text elements as tooltips.
     */
    hidePolygon (polygon, dots, tooltips) {
        polygon.removeClass('comparsion-chart__polygon--hovered');
        dots.forEach((dot) => dot.attr({ opacity: 0 }));
        tooltips.forEach((tooltip) => tooltip.attr({ opacity: 0 }));
    }

    /**
     * Creates toggle controls (checkboxes) for each material, allowing showing/hiding polygons.
     * Handles the "show all" functionality and keyboard accessibility.
     * @param {Array} datasets - The polygon datasets.
     * @param {Array} palette - The color palette for polygons.
     */
    setupControls (datasets, palette) {
        const $toggleList = this.$comparisonChart.querySelector('[data-comparsion-chart="toogle-list"]');
        const $toggleAll = this.$comparisonChart.querySelector('[data-comparsion-chart="toggle-all"]');

        datasets.forEach((dataset) => {
            const label = document.createElement('label');
            const paletteEntry = palette[dataset.rawIndex % palette.length];
            label.className = 'comparison-chart__control-item';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.className = 'comparison-chart__checkbox';
            checkbox.checked = true;
            checkbox.dataset.index = dataset.rawIndex;

            const span = document.createElement('span');
            span.className = 'comparison-chart__checkbox-span';
            span.style.color = paletteEntry.stroke;
            span.style.backgroundColor = this.hexToRgba(paletteEntry.stroke, 0.1);
            span.style.border = `1px solid ${paletteEntry.stroke}`;

            const text = document.createElement('span');
            text.textContent = dataset.name;

            label.appendChild(checkbox);
            label.appendChild(span);
            label.appendChild(text);

            // Add mouse/keyboard events for accessibility and hover highlighting
            label.addEventListener('mouseenter', () => {
                const entry = this.polygonRegistry[dataset.rawIndex];
                this.showPolygon(entry.polygon, entry.dots, entry.tooltips);
            });

            label.addEventListener('mouseleave', () => {
                const entry = this.polygonRegistry[dataset.rawIndex];
                this.hidePolygon(entry.polygon, entry.dots, entry.tooltips);
            });

            label.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    checkbox.checked = !checkbox.checked;
                    checkbox.dispatchEvent(new Event('change'));
                }
            });

            $toggleList.appendChild(label);

            // Checkbox toggling shows/hides the related polygon and its elements
            checkbox.addEventListener('change', () => {
                this.deactivateAll();
                const entry = this.polygonRegistry[dataset.rawIndex];
                const visible = checkbox.checked;
                entry.polygon.attr({ opacity: visible ? 1 : 0, 'pointer-events': visible ? 'auto' : 'none' });
                entry.dots.forEach((dot) => dot.attr({ opacity: 0, 'pointer-events': visible ? 'auto' : 'none' }));
                entry.tooltips.forEach((tooltip) => tooltip.attr({ opacity: 0, 'pointer-events': visible ? 'auto' : 'none' }));
                $toggleAll.checked = [...$toggleList.querySelectorAll('input[type="checkbox"]')].every((checkbox) => checkbox.checked);
            });
        });

        // Toggle all checkboxes at once
        $toggleAll.addEventListener('change', () => {
            this.deactivateAll();
            const checked = $toggleAll.checked;
            $toggleList.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
                checkbox.checked = checked;
                checkbox.dispatchEvent(new Event('change'));
            });
        });

        // Accessibility: Keyboard activation on the "show all" checkbox label
        const $toggleAllLabel = $toggleAll.closest('label');
        if ($toggleAllLabel) {
            $toggleAllLabel.addEventListener('keydown', (e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    $toggleAll.checked = !$toggleAll.checked;
                    $toggleAll.dispatchEvent(new Event('change'));
                }
            });
        }
    }

    /**
     * Converts a hex color code to an rgba string.
     * @param {string} hex - The hex color value (e.g. "#3692eb").
     * @param {number} alpha - The alpha (opacity) component (0..1).
     * @returns {string} The rgba color value.
     */
    hexToRgba (hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /**
     * Calculates the area of a polygon given its vertices.
     * Used for sorting polygons by size (bigger first, so smaller are visible in front).
     * @param {Array} points - List of [x, y] pairs representing polygon vertices.
     * @returns {number} The computed polygon area (positive value).
     */
    polygonArea (points) {
        let area = 0;
        const count = points.length;

        // Use the shoelace formula for area calculation
        points.forEach((point, i) => {
            const next = points[(i + 1) % count];
            area += point[0] * next[1];
            area -= next[0] * point[1];
        });

        return Math.abs(area / 2);
    }

    /**
     * Deactivates (hides) all polygons, their dots and tooltips on the chart.
     */
    deactivateAll () {
        Object.values(this.polygonRegistry).forEach(({ polygon, dots, tooltips }) => {
            this.hidePolygon(polygon, dots, tooltips);
        });
    }
}

export { ComparisonChart };

// Loads SVG.js, then finds and initializes all comparison charts in the context.
window.addEventListener('content.loaded', (e) => {
    const $context = e.detail.$context;
    if ($context) {
        loadScript('https://cdn.jsdelivr.net/npm/@svgdotjs/svg.js@3.2.5/dist/svg.min.js').then((data) => {
            const $$comparisonChart = $context.querySelectorAll('[data-comparsion-chart="root"]');
            $$comparisonChart.forEach((chartElement) => {
                const chart = new ComparisonChart(chartElement);
                chart.initialize();
            });
        });
    }
});
